import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import { get, isEqual, map } from 'lodash'
import moment from 'moment'
import { Button, CircularProgress } from '@material-ui/core'
import qs from 'querystring'
import asyncThrottler from '@scoir/async-throttler'
import { Provider, Month, Week, ControlPanel, LensControl } from '@scoir/react-calendar'
import Icon from '@scoir/components-core/dist/components/Icon'
import PendingSecondary from '@scoir/brand-icons/dist/PendingSecondary'

import api from 'common/api'
import { actions as calendarUpcomingVisitsActions } from 'store/upcoming-visits'
import { selectVisibleCalendars } from 'store/linked-calendars'
import { actions as userActions } from 'store/user'
import { actions as snackbarActions } from 'store/snackbar'
import leftArrowIcon from 'icons/keyboard-arrow-left.svg'
import rightArrowIcon from 'icons/keyboard-arrow-right.svg'
import { WeekView as CalendarEventWeekView } from './CalendarEvent'
import MonthDayEvents from './MonthDayEvents'
import TimeZoneInfoModal from './TimeZoneInfoModal'
import './index.scss'

const parseSearch = (search) => {
  search = search.replace(/^\?/, '')
  return qs.parse(search)
}

export class VisitsViewRoute extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      focus: moment(),
      isLoading: false,
      visits: [],
      showTimeZoneInfoModal: this.props.showTimeZoneInfoModal,
    }
  }
  componentDidMount() {
    document.title = 'Scoir - Visits'
    const focus = this.createFocusMoment(this.props.location)
    this.throttler = asyncThrottler.create((state) => this.setState(state))
    this.loadVisits({ selectedSchools: this.props.selectedSchools, focus })
  }
  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps.selectedSchools, this.props.selectedSchools)) {
      this.loadVisits({ selectedSchools: this.props.selectedSchools })
    }
    if (!isEqual(prevProps.linkedCalendars, this.props.linkedCalendars)) {
      this.loadVisits({ linkedCalendars: this.props.linkedCalendars })
    }
    if (prevProps.location.search !== this.props.location.search) {
      const focus = this.createFocusMoment(this.props.location)
      this.loadVisits({ focus })
    }
    if (!isEqual(prevProps.lens, this.props.lens)) {
      this.loadVisits({ lens: this.props.lens })
    }
    if (!isEqual(prevProps.typeFilters, this.props.typeFilters)) {
      this.loadVisits({ typeFilters: this.props.typeFilters })
    }
  }
  loadVisits({
    selectedSchools = this.props.selectedSchools,
    linkedCalendars = this.props.linkedCalendars,
    focus = this.state.focus,
    lens = this.props.lens,
    typeFilters = this.props.typeFilters,
  } = {}) {
    const startDate = moment(focus).startOf(lens).startOf('week').format()
    const endDate = moment(focus).endOf(lens).endOf('week').format()
    const params = qs.stringify({
      state: ['Draft', 'Open', 'Confirmed', 'Expired'],
      calendars: map(linkedCalendars, 'id'),
      ids: map(selectedSchools, 'Id'),
      startDate,
      endDate,
      virtual: typeFilters.virtual,
    })
    this.throttler.execute(
      api
        .get(`/api/highschools/calendar/visits?${params}`)
        .then((response) => {
          if (response.Error) {
            this.props.addSnack(response.Error, {
              id: 'view-visits-error',
              actionText: 'dismiss',
            })
          }
          return response.Visits
        })
        .then((rawVisits) => {
          this.props.fetchUpcomingVisits(this.props.userId)
          const visits = map(rawVisits, (v) => {
            const visitId = v.Id || v.SyncId
            return {
              ...v,
              Id: visitId,
            }
          })
          this.setState({
            visits,
            focus,
          })
        }),
    )
  }
  changeLens(lens) {
    const ROOT_PATH = import.meta.env.BASE_URL
    const { history, location } = this.props
    history.replace(`${ROOT_PATH}/recruit/visits/view/${lens}${location.search}`)
  }
  createFocusMoment(location) {
    const query = parseSearch(location.search)
    if (query.focus) {
      return moment(query.focus, 'YYYYMMDD')
    }
    return this.state.focus
  }
  updateFocus(focus) {
    const { history, lens } = this.props
    const focusTime = focus.format('YYYYMMDD')
    history.replace(`${import.meta.env.BASE_URL}/recruit/visits/view/${lens}?focus=${focusTime}`)
  }
  onHideTimeZoneModal(optOut) {
    if (optOut) {
      api.post('/api/user_ui_preference/timezone_confirmed', true).then(() => {
        this.props.fetchUser()
      })
    }
    this.setState({
      showTimeZoneInfoModal: false,
    })
  }
  render() {
    const FocusControl = this.props.lens === 'month' ? Month.FocusControl : Week.FocusControl
    return (
      <div className="route-visits">
        <ControlPanel className="calendar-cpanel-wrapper">
          <div className="calendar-cpanel">
            <div className="third-wrapper">
              <div className="lens-control-wrapper">
                <LensControl
                  lens={this.props.lens}
                  isVisible={['week', 'month']}
                  onLensChange={(lens) => this.changeLens(lens)}
                />
              </div>
              <div className="spinner-wrapper">
                {this.state.isProcessing && (
                  <CircularProgress
                    role="status"
                    aria-label="loading"
                    size="25px"
                  />
                )}
              </div>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => this.updateFocus(moment())}
              >
                Today
              </Button>
            </div>
            <div className="third-wrapper">
              <div className="focus-control-wrapper">
                <div className="focus-control-inline">
                  <FocusControl
                    focusDate={this.state.focus}
                    priorElem={
                      <Icon
                        def={leftArrowIcon}
                        size="md"
                      />
                    }
                    aheadElem={
                      <Icon
                        def={rightArrowIcon}
                        size="md"
                      />
                    }
                    onFocusDateChange={(newFocus) => this.updateFocus(newFocus)}
                  />
                </div>
              </div>
            </div>
            <div className="viewing-timezone">
              <div>
                <PendingSecondary
                  size="md"
                  className="clock-icon"
                />
              </div>
              <div>
                <span className="timezone-info">Events displayed in your local time</span>
                <br />
                <span>Click a visit to view {"host's"} timezone</span>
              </div>
            </div>
          </div>
        </ControlPanel>
        <Provider>
          {this.props.lens === 'week' && (
            <Week.View
              className="calendar-week-view"
              events={this.state.visits}
              focusDate={this.state.focus}
              eventComponent={(props) => (
                <CalendarEventWeekView
                  onEventChange={() => this.loadVisits()}
                  {...props}
                />
              )}
            />
          )}
          {this.props.lens === 'month' && (
            <Month.View
              events={this.state.visits}
              focusDate={this.state.focus}
              eventDayComponent={(props) => {
                return (
                  <MonthDayEvents
                    onEventChange={() => this.loadVisits()}
                    {...props}
                  />
                )
              }}
            />
          )}
        </Provider>
        <TimeZoneInfoModal
          show={this.state.showTimeZoneInfoModal}
          onDismiss={() => this.setState({ showTimeZoneInfoModal: false })}
          onHide={(optOut) => this.onHideTimeZoneModal(optOut)}
        />
      </div>
    )
  }
}

VisitsViewRoute.propTypes = {
  selectedSchools: PropTypes.arrayOf(PropTypes.object).isRequired,
  linkedCalendars: PropTypes.array.isRequired,
  fetchUpcomingVisits: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  lens: LensControl.propTypes.lens,
  showTimeZoneInfoModal: PropTypes.bool.isRequired,
  fetchUser: PropTypes.func.isRequired,
  user: PropTypes.object,
  addSnack: PropTypes.func.isRequired,
  typeFilters: PropTypes.object.isRequired,
}

export default connect(
  (state) => ({
    selectedSchools: state.visitSelectedSchools,
    linkedCalendars: selectVisibleCalendars(state),
    userId: state.user.record.Id,
    user: state.user.record,
    showTimeZoneInfoModal: !(
      state.user.record.Preferences && get(state.user.record.Preferences, 'VisitsInfoTimezoneConfirmed', true)
    ),
    typeFilters: state.calendarVisitTypeFilters,
  }),
  {
    fetchUpcomingVisits: calendarUpcomingVisitsActions.fetchUpcomingVisits,
    fetchUser: userActions.fetchUser,
    addSnack: snackbarActions.addItem,
  },
)(VisitsViewRoute)
